import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { KineticText } from "../components/KineticText";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";
import { Spinner } from "../components/Spinner";
import { Lightbox } from "../components/Lightbox";
import { BlurImage } from "../components/BlurImage";
import { PlayBadge } from "../components/PlayBadge";
import { RichText } from "../components/RichText";
import { fetchProject, fetchProjects, fetchSiteContent, mediaUrl, sizedUrl, srcSetFor } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const spanFor = (i, img) =>
  (i % 5 === 0 && img && img.w && img.h && img.w >= img.h ? "md:col-span-2" : "md:col-span-1");

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, L, tc } = useLang();
  const [project, setProject] = useState(null);
  const [all, setAll] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lbIndex, setLbIndex] = useState(null);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([fetchProject(id), fetchProjects(), fetchSiteContent()])
      .then(([p, list, c]) => { setProject(p); setAll(list); setContent(c); })
      .catch((e) => { console.error("project load failed:", e); if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [id]);

  // A project always opens at the top (scroll restoration is homepage-only).
  useEffect(() => {
    if (loading) return;
    const toTop = () => { if (window.__lenis) { window.__lenis.resize(); window.__lenis.scrollTo(0, { immediate: true }); } window.scrollTo(0, 0); };
    const r = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(r);
  }, [loading, id]);

  if (loading) return <Spinner />;

  if (notFound || !project) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="project-not-found">
        <p className="font-display text-5xl">{t("not_found")}</p>
        <Link to="/" className="font-mono text-xs tracking-[0.2em] uppercase nav-link">← {t("back_to_projects")}</Link>
      </main>
    );
  }

  const idx = all.findIndex((p) => p.slug === project.slug);
  const next = all[(idx + 1) % all.length] || project;
  const prev = all[(idx - 1 + all.length) % all.length] || project;
  const gallery = project.images;

  return (
    <main data-testid="project-detail-page">
      <section ref={heroRef} className="relative h-[92vh] overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          {project.cover_type === "video" ? (
            <video src={mediaUrl(project.cover)} poster={project.cover_poster ? mediaUrl(project.cover_poster) : undefined}
              autoPlay muted loop playsInline
              style={{ objectPosition: project.cover_position || "center" }}
              className="w-full h-full object-cover vsl-shimmer" />
          ) : (
            <img src={sizedUrl(project.cover, 1920)} srcSet={srcSetFor(project.cover, [800, 1200, 1920])} sizes="100vw" alt={project.name}
              style={{ objectPosition: project.cover_position || "center" }}
              className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-[var(--vsl-bg)]/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--vsl-bg)] via-transparent to-[var(--vsl-bg)]/30" />
        </motion.div>

        <div className="relative h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end py-24">
          <div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
              className="font-mono text-[11px] tracking-[0.4em] uppercase text-[var(--vsl-limestone)] mb-5">
              {String(idx + 1).padStart(2, "0")} — {(project.categories && project.categories.length ? project.categories : [project.category]).filter(Boolean).map((c) => tc(c)).join(" · ")}
            </motion.p>
            <KineticText lines={[L(project, "name")]} className="font-display text-[14vw] md:text-[8vw] leading-[0.85] tracking-[-0.01em] text-[var(--vsl-on-media)]" />
          </div>
        </div>
      </section>

      <section data-testid="project-info" className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-8">
            <RichText as="p" html={L(project, "description")}
              className="font-roboto text-base md:text-lg lg:text-xl font-light leading-relaxed whitespace-pre-line max-w-[52ch] lg:max-w-[68ch] xl:max-w-[84ch]" />
          </Reveal>
          <div className="md:col-span-3 md:col-start-10 space-y-8">
            <Reveal className="border-t border-[var(--vsl-border)] pt-4">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] mb-2">{t("detail_category")}</p>
              <p className="font-mono text-sm">{(project.categories && project.categories.length ? project.categories : [project.category]).filter(Boolean).map((c) => tc(c)).join(", ")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {gallery.length > 0 && (
        <section data-testid="project-gallery" className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
          {gallery.map((img, i) => (
            <motion.figure
              key={img.id}
              data-testid={`gallery-image-${i}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              style={{ aspectRatio: img.w && img.h ? `${img.w} / ${img.h}` : undefined }}
              onClick={() => setLbIndex(project.images.findIndex((im) => im.id === img.id))}
              className={`col-span-1 ${spanFor(i, img)} max-h-[86vh] overflow-hidden ${img.type === "video" ? "vsl-shimmer" : "bg-[#141312]"} group cursor-zoom-in relative`}
            >
              {img.type === "video" ? (
                <>
                  {img.poster ? (
                    <BlurImage lqip={img.lqip} wrapperClassName="w-full h-full"
                      src={sizedUrl(img.poster, 1200)} srcSet={srcSetFor(img.poster, [480, 800, 1200, 1600])} sizes="(max-width: 767px) 100vw, 50vw" alt={`${project.name} ${i + 2}`} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]" />
                  ) : (
                    <video src={mediaUrl(img.url)} muted playsInline preload="metadata"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]" />
                  )}
                  <PlayBadge size={64} />
                </>
              ) : (
                <BlurImage lqip={img.lqip} wrapperClassName="w-full h-full"
                  src={sizedUrl(img.url, 1200)} srcSet={srcSetFor(img.url, [480, 800, 1200, 1600])} sizes="(max-width: 767px) 100vw, 50vw" alt={`${project.name} ${i + 2}`} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]" />
              )}
            </motion.figure>
          ))}
        </section>
      )}

      <section data-testid="project-nav" className="mx-auto max-w-[1600px] px-6 md:px-12 py-28 grid grid-cols-2 gap-6 border-t border-[var(--vsl-border)] mt-24">
        <button data-testid="prev-project" onClick={() => navigate(`/project/${prev.slug}`)} className="group text-left">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] mb-3">
            <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> {t("prev")}
          </span>
          <p className="font-display text-3xl md:text-5xl group-hover:text-[var(--vsl-limestone)] transition-colors">{L(prev, "name")}</p>
        </button>
        <button data-testid="next-project" onClick={() => navigate(`/project/${next.slug}`)} className="group text-right flex flex-col items-end">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] mb-3">
            {t("next")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </span>
          <p className="font-display text-3xl md:text-5xl group-hover:text-[var(--vsl-limestone)] transition-colors">{L(next, "name")}</p>
        </button>
      </section>

      <Footer content={content} />

      <Lightbox
        images={project.images}
        index={lbIndex}
        onClose={() => setLbIndex(null)}
        onNavigate={setLbIndex}
      />
    </main>
  );
}
